import { useState } from 'react';
import { Button } from './ui/Button';
import { 
  validateBackupFile, 
  getBackupSummary, 
  restoreFromBackup,
  BackupData
} from '../services/backupRestore';
import toast from 'react-hot-toast';

interface BackupRestoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const BackupRestoreModal = ({ isOpen, onClose, onSuccess }: BackupRestoreModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [backupData, setBackupData] = useState<BackupData | null>(null);
  const [loading, setLoading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    try {
      const text = await selectedFile.text();
      const data = JSON.parse(text);
      
      const validation = validateBackupFile(data);
      if (!validation.valid) {
        toast.error(validation.error || 'Archivo inválido');
        return;
      }

      setFile(selectedFile);
      setBackupData(data);
      toast.success('Archivo cargado correctamente');
    } catch (error) {
      toast.error('Error leyendo el archivo. Asegúrate de que sea un JSON válido.');
    }
  };

  const handleRestore = async () => {
    if (!backupData) {
      toast.error('Primero selecciona un archivo');
      return;
    }

    // Confirmar acción
    const confirmMessage = 
      '⚠️ ADVERTENCIA CRÍTICA ⚠️\n\n' +
      'Esto BORRARÁ TODOS los datos actuales y los reemplazará con el backup.\n\n' +
      '¿Estás ABSOLUTAMENTE seguro?\n\n' +
      'Esta acción NO se puede deshacer.';
    
    if (!window.confirm(confirmMessage)) return;

    // Doble confirmación
    const doubleConfirm = window.prompt(
      'Para confirmar, escribe "RESTAURAR" en mayúsculas:'
    );
    
    if (doubleConfirm !== 'RESTAURAR') {
      toast.error('Restauración cancelada');
      return;
    }

    setLoading(true);
    try {
      const result = await restoreFromBackup(backupData);
      
      if (result.success) {
        toast.success(
          `✅ Restauración completada exitosamente:\n\n` +
          `• Usuarios: ${result.usersRestored}\n` +
          `• Propiedades: ${result.propertiesRestored}\n` +
          `• Datos mensuales: ${result.monthlyStatsRestored}`,
          { duration: 6000 }
        );
        onSuccess();
        handleClose();
      } else {
        toast.error(
          `⚠️ Restauración completada con errores:\n\n${result.errors.slice(0, 3).join('\n')}`,
          { duration: 8000 }
        );
      }
    } catch (error: any) {
      toast.error(`❌ Error crítico: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setBackupData(null);
    onClose();
  };

  const summary = backupData ? getBackupSummary(backupData) : null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full relative z-50">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">🔄 Restaurar Backup Completo</h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
        {/* Selección de archivo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            📁 Seleccionar archivo de backup (.json)
          </label>
          <input
            type="file"
            accept=".json"
            onChange={handleFileChange}
            disabled={loading}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0
              file:text-sm file:font-semibold
              file:bg-red-50 file:text-red-700
              hover:file:bg-red-100
              disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {file && (
            <p className="mt-2 text-sm text-green-600">
              ✅ Archivo cargado: {file.name}
            </p>
          )}
        </div>

        {/* Resumen del backup */}
        {summary && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-3">📊 Contenido del backup:</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-blue-700">Usuarios:</span>
                <span className="font-semibold text-blue-900">{summary.users}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700">Propiedades:</span>
                <span className="font-semibold text-blue-900">{summary.properties}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700">Datos mensuales:</span>
                <span className="font-semibold text-blue-900">{summary.monthlyStats}</span>
              </div>
              <div className="flex justify-between border-t border-blue-200 pt-2 mt-2">
                <span className="text-blue-700">Fecha del backup:</span>
                <span className="font-semibold text-blue-900">{summary.timestamp}</span>
              </div>
            </div>
          </div>
        )}

        {/* Advertencia */}
        {backupData && (
          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-bold text-red-800">⚠️ ADVERTENCIA CRÍTICA</h3>
                <div className="mt-2 text-sm text-red-700 space-y-1">
                  <p>• Se BORRARÁN <strong>TODOS</strong> los datos actuales</p>
                  <p>• Se reemplazarán con los datos del backup</p>
                  <p>• Esta acción <strong>NO se puede deshacer</strong></p>
                  <p className="mt-2 font-semibold">Asegúrate de tener un backup reciente antes de continuar.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Botones */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleRestore}
            disabled={!backupData || loading}
            className="bg-red-600 hover:bg-red-700"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Restaurando...
              </>
            ) : (
              '🔄 Restaurar Todo'
            )}
          </Button>
        </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
